import type { ReactElement, RefCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { FilterItemClearButton } from "app/pages/sales-intelligence/common-components/filters-panel";
import { StyledFilterItemCardItemTitle } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemCard/styles";
import { TruncatedTextWithTooltip } from "app/pages/sales-intelligence/common-components/TruncatedText";
import type { IFilterManagerModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-manager/types";
import type {
    FilterInstanceType,
    IFilterInstance,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

const SELECTED_CRITERIA_SECTION_AUTOMATION = "selected-criteria-section";

type Props = {
    filtersManager: IFilterManagerModel<LeadsSearchAllFiltersTypes>;
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    isActionEnabled?: boolean;
    isChipActionEnabled?: boolean;
    isEditEnabled?: boolean;
    showClearButton?: boolean;
    emptyMessage?: string;
    registerSectionRef?: (key: string) => RefCallback<HTMLElement>;
};

export const SelectedLeadsFilterCriteriaSections = ({
    filtersManager,
    filtersList,
    isActionEnabled = false,
    isChipActionEnabled,
    isEditEnabled,
    showClearButton = false,
    emptyMessage,
    registerSectionRef,
}: Props) => {
    const { translate } = useTranslate();
    const chipActionsEnabled = isChipActionEnabled ?? isActionEnabled;
    const editActionsEnabled = isEditEnabled ?? isActionEnabled;

    const sections = filtersList
        .map((config): ReactElement | null => {
            if (!filtersManager.isFilterDirty(config.key)) {
                return null;
            }

            const typedConfig = config as unknown as FilterInstanceType<LeadsSearchAllFiltersTypes, unknown, unknown>;
            const FilterResultView = typedConfig.filterResultView;

            if (!FilterResultView) {
                return null;
            }

            const filterDtoValue = filtersManager.getFilterDtoValue(config.key);
            const value = typedConfig.filter.fromApiValue(filterDtoValue);
            const title = typedConfig.title;
            const titleText = title ? (is.string(title) ? title : translate(title.key, title.replacements)) : null;

            return (
                <Stack
                    key={String(config.key)}
                    ref={registerSectionRef?.(String(config.key))}
                    gap="8px"
                    padding="12px"
                    background={colorsPalettes.bluegrey["100"]}
                    borderRadius="8px"
                    data-automation={SELECTED_CRITERIA_SECTION_AUTOMATION}
                    data-filter-key={String(config.key)}
                >
                    {titleText || showClearButton ? (
                        <Flex
                            width="100%"
                            alignItems="center"
                            justifyContent={titleText ? undefined : "flex-end"}
                            gap="6px"
                        >
                            {titleText ? (
                                <StyledFilterItemCardItemTitle>
                                    <TruncatedTextWithTooltip
                                        fontVariant="button"
                                        color={colorsPalettes.bluegrey["600"]}
                                    >
                                        {titleText}
                                    </TruncatedTextWithTooltip>
                                </StyledFilterItemCardItemTitle>
                            ) : null}
                            {showClearButton ? (
                                <FilterItemClearButton onClick={() => filtersManager.resetFilter(config.key)} />
                            ) : null}
                        </Flex>
                    ) : null}
                    <FilterResultView
                        filter={typedConfig.filter}
                        value={value}
                        isExpanded={false}
                        isActionEnabled={chipActionsEnabled}
                        isEditEnabled={editActionsEnabled}
                        onFilterUpdate={(nextValue) =>
                            filtersManager.normalizeAndUpdateFilter(
                                config.key,
                                nextValue as LeadsSearchAllFiltersTypes[typeof config.key],
                            )
                        }
                    />
                </Stack>
            );
        })
        .filter((section): section is ReactElement => Boolean(section));

    if (sections.length === 0) {
        if (!emptyMessage) {
            return null;
        }
        return (
            <Flex>
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.carbon["300"]}
                >
                    {emptyMessage}
                </Text>
            </Flex>
        );
    }

    return <Stack gap="8px">{sections}</Stack>;
};
